/**
 * Marketing / attribution query params.
 *
 * Single source of truth for the set of params we treat as attribution so the
 * booking-widget redirect, the app-wide capture in `SearchParamSync`, and any
 * downstream lead-attribution code all agree on the same keys.
 */

import { getCookie } from '@/utils/cookies'

export const MARKETING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'gad_source',
  'gclsrc',
  'ttclid',
  'click_id',
] as const

export type MarketingParamKey = (typeof MARKETING_PARAM_KEYS)[number]

export type MarketingParams = Partial<Record<MarketingParamKey, string>>

/** Anything with a `.get(name)` accessor — `URLSearchParams` or Next ReadonlyURLSearchParams. */
type ParamSource = { get(name: string): string | null } | null | undefined

/**
 * Pulls the present marketing params out of a query-param source.
 * Keys with no value (or an empty string) are omitted.
 */
export const extractMarketingParams = (source: ParamSource): MarketingParams => {
  const result: MarketingParams = {}

  if (!source) return result

  for (const key of MARKETING_PARAM_KEYS) {
    const value = source.get(key)
    if (value) result[key] = value
  }

  return result
}

/**
 * Copies any present marketing params from `source` into `target`, mutating and
 * returning `target`. Mirrors the existing inline `coupon` forwarding pattern.
 */
export const appendMarketingParams = (
  target: URLSearchParams,
  source: ParamSource,
): URLSearchParams => {
  if (!source) return target

  for (const key of MARKETING_PARAM_KEYS) {
    const value = source.get(key)
    if (value) target.set(key, value)
  }

  return target
}

/** True when at least one marketing param is present. */
export const hasMarketingParams = (params: MarketingParams): boolean =>
  MARKETING_PARAM_KEYS.some((key) => Boolean(params[key]))

/** The persistent first-party click cookies set by the ad pixels (Meta/Google). */
export interface ClickCookies {
  fbc?: string
  fbp?: string
  gcl_aw?: string
}

/**
 * Reads the persistent click cookies the ad platforms match on. These are set
 * by the Meta Pixel / gtag (via GTM) and survive URL-param stripping, unlike the
 * raw click ids. Returns only the cookies that are present.
 */
export const readClickCookies = (): ClickCookies => {
  const cookies: ClickCookies = {}

  const fbc = getCookie('_fbc')
  const fbp = getCookie('_fbp')
  const gclAw = getCookie('_gcl_aw')

  if (fbc) cookies.fbc = fbc
  if (fbp) cookies.fbp = fbp
  if (gclAw) cookies.gcl_aw = gclAw

  return cookies
}
